import {
  get,
  has,
  find,
  forEach,
  findIndex,
  isEmpty,
  some,
  uniq,
  every,
  isBoolean,
  map,
  invoke,
  sortBy,
  indexOf,
} from 'lodash';

import {
  QUESTION_NPS,
  QUESTION_MATRIX_MULTIPLE,
  QUESTION_MATRIX_SINGLE,
  QUESTION_CONVERSATIONAL,
  ANSWER_OTHER,
  ANSWER_NONE_OF_THE_ABOVE,
  GROUP_TYPE_AB,
  GROUP_TYPE_VAN_WESTENDORP,
  GROUP_TYPE_CONJOINT,
  GROUP_TYPE_MAXDIFF,
  GROUP_TYPE_LOOP_AND_MERGE,
  TRANSFORMATION_DATA_TYPE_JSON,
  SURVEY_STATE,
  CONVERSATIONAL_AI_VALUE_STATUSES,
  SHUFFLE_NONE,
} from '../../constants';
// eslint-disable-next-line import/no-cycle
import checkAndEnterSurvey from './checkAndEnterSurvey';

import QuestionGroup, { UNSUCCESSFUL_GROUP_SETUP } from './QuestionGroup';
import {
  transformAnswersForBackendStorage,
  transformAnswersForADL,
  transformValuesToString,
  encodeTextAnswers,
  shuffleArray,
  extractQuotaStrategies,
} from '../../utils';
import {
  questionnaireLogicService,
  questionnairePathTraversal,
  FinalNode,
  Matcher,
  KnowledgeKeys,
  FinalGroupNode,
  StatementMergeItemTypes,
  qlUnifyService,
} from '../../logic';
import request from '../../request';
import { publicAssetsPrefix } from '../../../../../configuration';

class Survey {
  /* eslint-disable camelcase */
  constructor(
    {
      s_id: surveyId,
      questions = [],
      lang,
      user_lang,
      survey_price,
      reward_name,
      reward_value,
      survey_class: surveyClass = '',
      survey_loi,
      survey_ir,
      remaining_completes,
      third_party_provider: thirdPartyProvider = null,
      isFeedbackSurvey = false,
      distribution_id,
      survey_session_id,
      url,
      questionnaireLogic,
      screeningLogic,
      respondentKnowledge,
      questionGroups,
      questionGroupShuffle,
      isPartialSurvey,
      hasCustomTerms = false,
      custom_end_message,
      is_distribution_link = false,
      is_external_audience = false,
      userTaxonomy,
      no_check: noCheck,
      surveyTheme,
      quotas,
      quotaAssigned,
      revisionId,
    },
    options = {},
  ) {
    this.props = {
      surveyId,
      questions,
      lang,
      user_lang,
      survey_price,
      reward_name,
      reward_value,
      surveyClass,
      survey_loi,
      survey_ir,
      remaining_completes,
      thirdPartyProvider,
      isFeedbackSurvey,
      url,
      respondentKnowledge,
      questionGroups,
      questionGroupShuffle,
      distribution_id,
      surveySessionId: survey_session_id,
      isPartialSurvey,
      hasCustomTerms,
      custom_end_message,
      is_distribution_link,
      is_external_audience,
      noCheck,
      surveyTheme,
      revisionId,
    };
    this.checkAndEnterSurveyHasBeenCalledOnce =
      !!isFeedbackSurvey || Pollfish.helper.isDebugMode();

    const quotaStrategies = extractQuotaStrategies(questions);

    let ql = questionnaireLogicService.migrate(questionnaireLogic);
    let sql = questionnaireLogicService.migrate(screeningLogic);

    if (questionGroupShuffle) {
      if (sql === undefined) {
        const split = qlUnifyService.qlSplit(ql);
        ql = split.ql;
        sql = split.sql;
      }

      const shuffledQuestionnaire = shuffleArray(
        ql.questionnaire.nodes,
        questionGroupShuffle ?? 0,
      );
      const transformedShuffledQuestionnaire = questionnaireLogicService.transformQLNodesForUseWithQuestionnaireLogic(
        shuffledQuestionnaire,
      );

      let shuffledQuestionnaireLogic = questionnaireLogicService.buildDefaultQuestionnaireLogic(
        { surveyId, nodes: transformedShuffledQuestionnaire },
        { defaultMatcher: Matcher.Equal, expandConditions: false },
      );

      shuffledQuestionnaireLogic = questionnaireLogicService.appendStatementsByTypeToQuestionnaireLogic(
        ql,
        shuffledQuestionnaireLogic,
        [
          StatementMergeItemTypes.DefaultHideAnswer,
          StatementMergeItemTypes.DefaultHideNode,
        ],
      );

      ql = questionnaireLogicService.migrate(shuffledQuestionnaireLogic);
    }

    if (ql || sql)
      this.props.questionnaireLogic = qlUnifyService.qlUnify(ql, sql);

    /* eslint-enable camelcase */

    this.options = options;

    this.savedAnswers = [];
    this.questionOrder = -1;

    Pollfish.store.initRespondentKnowledge(
      respondentKnowledge,
      userTaxonomy,
      quotas,
      quotaAssigned,
      quotaStrategies,
    );

    // handled question/answer ordering and some more
    this.preprocess();

    Pollfish.store.answeredQuestions = [];

    this.hasDownloadedImages = false;

    // updates Pollfish.helper.json properties with received onces in case an override is needed
    // and i forgot to make it somewhere
    this.updateFallbackValues();

    // we generate a partial survey when a third party survey returns from a redirect on sdks,
    // where we are missing most of the survey information.
    // We needed a survey class instance to handle survey_session_id calls,
    // so we created a partial survey
    if (isPartialSurvey) {
      return this;
    }

    if (!isFeedbackSurvey) {
      this.props.questions = questions.filter(
        ({ isScreening }) => !isScreening,
      );
      this.setupGroups();
      Pollfish.screening.setQuestions(questions);
    }

    // update debug flags
    if (!Pollfish.helper.debug) {
      Pollfish.helper.debug = {};
    }
    Pollfish.helper.debug.questions = questions;
    this.setupProgressBar();
    Pollfish.helper.lastSurveyId = surveyId;
    Pollfish.helper.isInternalSurvey =
      surveyClass.toLowerCase().indexOf('pollfish/internal') > -1;

    return this;
  }

  async start({ startFromQuestion, sendAcceptedEvent = true } = {}) {
    Pollfish.ui.applyBrandedSurveyStyles();

    const { checkStatus = true } = this.options;
    const { surveyId, isFeedbackSurvey } = this.props;
    Pollfish.helper.getInitialHeight();
    // when starting a survey set the state as starting
    Pollfish.surveyState.setState(SURVEY_STATE.STARTING_SURVEY);
    Pollfish.surveyState.setFailedOutcomeReason(false);
    if (Pollfish.helper.closeButtonHidden) {
      Pollfish.ui.showCloseButtonAtHeader();
    }

    if (surveyId && !isFeedbackSurvey && sendAcceptedEvent) {
      const payload = {
        s_id: surveyId.toString(),
        distribution_id: this.getDistributionId().toString(),
      };

      Pollfish.mobile.interface.sendToServer(
        '/v2/device/set/survey/accept',
        payload,
        false,
      );
    }

    this.downloadImages();
    const continueToFirstQuestion = () => {
      this.getFirstStartingQuestion({ startFromQuestion })
        .then((question) => {
          this.showQuestion(question);
        })
        .catch((error) => {
          if (error === UNSUCCESSFUL_GROUP_SETUP) {
            Pollfish.helper.handleUnsuccesfullGroupSetup(error);
          } else {
            Pollfish.helper.handleGlobalPromiseError(error);
          }
        });
    };
    // check and enter survey first if needed, then get first question
    if (checkStatus) {
      checkAndEnterSurvey({
        onSurveyContinue: () => {
          continueToFirstQuestion();
        },
      });
    } else {
      continueToFirstQuestion();
    }
  }

  async getFirstStartingQuestion({ startFromQuestion }) {
    if (!startFromQuestion) {
      return this.getNextQuestion();
    }
    const question = startFromQuestion;
    let questionIndex = findIndex(
      this.props.questions,
      ({ id }) => id === question.id,
    );

    // if question is part of a display same page group,
    // then we need to find the first question of that group
    // in the questions array before the group shuffle
    if (question.groupRefId) {
      const groupDisplaySamePage = invoke(
        this.getDisplaySamePageGroupByQuestion(question),
        'isDisplaySamePage',
        false,
      );
      if (groupDisplaySamePage) {
        const groupQuestionsBeforeGroupSetup = this.props.questions.filter(
          (q) => q.groupRefId === question.groupRefId,
        );
        questionIndex = findIndex(
          this.props.questions,
          ({ refId }) => refId === groupQuestionsBeforeGroupSetup[0].refId,
        );
      }
    }

    if (Pollfish.helper.isPreviewFromScreeningQuestion) {
      questionIndex = findIndex(
        Pollfish.screening.getQuestions(),
        ({ id }) => id === question.id,
      );
    }
    if (questionIndex === -1) {
      const error = new Error(
        'Attempt to start from a question that does not exist',
      );
      Raven.captureException(error, {
        extra: Pollfish.helper.getErrorObject(),
      });
    }
    Pollfish.store.answeredQuestions = new Array(questionIndex);

    if (!Pollfish.helper.isPreviewFromScreeningQuestion && this.groups) {
      if (has(question, 'groupRefId')) {
        const group = find(this.groups, ['refId', question.groupRefId]);
        this.currentGroup = group;
      }
    }

    return question;
  }

  updateShuffledGroupLogic(group) {
    const { questionnaireLogic } = this.props;
    const { questions } = group;
    const orderedQuestionRefIds = questions.map((q) => q.refId);
    if (!questionnaireLogic) {
      return;
    }
    // update the order inside the questionnaire.nodes
    const qlGroup = find(questionnaireLogic.questionnaire.nodes, {
      id: group.refId,
    });
    qlGroup.nodes = sortBy(qlGroup.nodes, [
      (node) => indexOf(orderedQuestionRefIds, node.id),
    ]);
    // update the node statements to point to the new order
    forEach(orderedQuestionRefIds, (refId, index) => {
      const defaultStatement = questionnaireLogicService.findPassthroughStatementByNode(
        questionnaireLogic,
        refId,
      );
      defaultStatement.outcome.params.id =
        orderedQuestionRefIds[index + 1] || FinalGroupNode;
    });
  }

  setupGroups() {
    const { questionGroupShuffle } = this.props;
    const { questions, questionGroups = [] } = this.props;

    if (isEmpty(questionGroups)) {
      // nothing to do, move along
      return;
    }

    const questionsInGroups = questions.filter((question) =>
      has(question, 'groupRefId'),
    );
    const transformedGroups = [];
    const groupRefIds = [];

    forEach(questionsInGroups, (question) => {
      const { groupRefId } = question;
      if (!groupRefIds.includes(groupRefId)) {
        groupRefIds.push(groupRefId);
      }

      const transformedGroup = find(
        transformedGroups,
        ({ refId }) => refId === groupRefId,
      );
      if (!transformedGroup) {
        const group = find(questionGroups, ({ refId }) => refId === groupRefId);
        const questionGroup = new QuestionGroup({
          questions: [question],
          refId: groupRefId,
          id: group.id,
          shuffle: group.shuffle,
          attributeSet: group.attributeSet,
          type: group.groupType,
          displaySamePage: group.displaySamePage,
        });
        transformedGroups.push(questionGroup);
      } else {
        transformedGroup.addQuestion(question);
      }
    });

    // after we have finished creating the groups,
    // we need to shuffle the questions inside each group
    forEach(transformedGroups, (group) => {
      if (group.shuffleType !== SHUFFLE_NONE) {
        group.shuffleQuestions();
        this.updateShuffledGroupLogic(group);
      }
    });

    this.groupRefIds = groupRefIds;
    this.groups = shuffleArray(transformedGroups, questionGroupShuffle);
    if (Pollfish.helper.isDebugMode()) {
      this.setRandomisedConceptsForGroups();
    } else {
      // we are calling the backend to provide these values when we get each group
    }
    this.groupsSetUp = [];
  }

  setRandomisedConceptsForGroups() {
    this.groups.forEach((group) => {
      group.randomizeConcepts();
    });
  }

  // groups can be invalid until the check survey enter function is called (AB tests only)
  // NOTE: this is not used yet
  areGroupsValid() {
    const shouldCheckConcepts =
      this.isRegularPollfishSurvey() && !isEmpty(this.groups);
    if (!shouldCheckConcepts) {
      return true;
    }
    return every(this.groups, (group) => group.isValid());
  }

  showQuestion = (question) => {
    if (question) {
      Pollfish.ui.moveToQuestion(question);
      return;
    }
    Raven.captureMessage('no next question or url', {
      extra: { ...Pollfish.helper.getRedirectData() },
    });
    // @TODO: remove duplicate with thank you
    Pollfish.mobile.interface.setSurveyCompleted();

    // show thank you component on basic format
    Pollfish.ui.thankYouBasic();
  };

  getLastQuestion = () => {
    this.isFinished = true;
    return null;
  };

  downloadImages() {
    // TODO: if survey has expired, previous downloads is useless
    for (let i = 1; i < this.props.questions.length; i += 1) {
      const question = this.props.questions[i];
      this.downloadImagesForQuestion(question, i);
    }
  }

  downloadImagesForQuestion(question, questionIndex = 0) {
    const questionImageToShow =
      question.optimized_image_url || question.image_url;
    const hasImageToShow = Boolean(questionImageToShow);

    // download question image if it exists
    if (hasImageToShow) {
      const questionImage = new Image();
      questionImage.src = questionImageToShow;
    }

    // download answer images if they exist
    for (let i = question.answers.length - 1; i >= 0; i -= 1) {
      const answer = question.answers[i];
      const answerDefaultImage = answer.optimized_image_url || answer.image_url;
      if (answerDefaultImage) {
        const answerImage = new Image();
        answerImage.src = answerDefaultImage;
      }
    }

    // download question poster if video exists
    if (question.video) {
      const questionPoster = new Image();
      questionPoster.src = question.video.poster;
    }

    // download images only once the first question has been answered
    if (questionIndex > 0 && !this.hasDownloadedImages) {
      const orcaImage = new Image();
      orcaImage.src = `${publicAssetsPrefix}/img/orca.png`;

      const searchImage = new Image();
      searchImage.src = `${publicAssetsPrefix}/img/search.png`;

      const thankYouImage = new Image();
      thankYouImage.src = `${publicAssetsPrefix}/img/thankyou.png`;

      this.hasDownloadedImages = true;
    }
  }

  // download media image (AB Testing) selectedAttribute if groups exist
  // eslint-disable-next-line class-methods-use-this
  downloadSelectedAttributesImagesForQuestionGroup(questionGroup) {
    const { selectedConcepts, concepts, questions } = questionGroup;
    if (!selectedConcepts || !concepts) {
      return;
    }
    const { attributeValues } = find(concepts, ['refId', selectedConcepts[0]]);
    let conceptSelectedAttributes = [];
    questions.forEach(({ selectedAttributes }) => {
      if (selectedAttributes) {
        conceptSelectedAttributes = [
          ...conceptSelectedAttributes,
          ...selectedAttributes,
        ];
      }
    });
    conceptSelectedAttributes = uniq(conceptSelectedAttributes);
    conceptSelectedAttributes.forEach((conceptReferenceAttributeId) => {
      const attributeValue = find(attributeValues, [
        'attributeRefId',
        conceptReferenceAttributeId,
      ]);
      if (!isEmpty(attributeValue.media)) {
        const mediaAttributeImg = new Image();
        mediaAttributeImg.src = attributeValue.media.thumbnail;
      }
    });
  }

  isFeedbackSurvey() {
    return this.props.isFeedbackSurvey;
  }

  getSurveyTheme() {
    return get(this.props, 'surveyTheme');
  }

  isDistributionLink() {
    return this.props.is_distribution_link;
  }

  isRegularPollfishSurvey() {
    const { surveyClass } = this.props;
    return surveyClass === 'Pollfish/Basic' || surveyClass === 'Pollfish';
  }

  setProps(newProps) {
    this.props = {
      ...this.props,
      ...newProps,
    };
  }

  isInternalSurvey() {
    const { surveyClass } = this.props;
    return surveyClass.toLowerCase().indexOf('pollfish/internal') > -1;
  }

  setupProgressBar() {
    const { questions, questionnaireLogic } = this.props;
    const questionsLength = questionnaireLogic
      ? questionnairePathTraversal.findMaxLength(questionnaireLogic)
      : questions.length;
    Pollfish.store.totalQuestions = questionsLength;
  }

  preprocess() {
    const { questions } = this.props;
    let totalWords = '';
    let surveyLOI;

    for (let i = 0; i < questions.length; i += 1) {
      const question = questions[i];
      totalWords += question.prompt;

      question.answers.sort((a, b) => {
        if (a.order > b.order) {
          return 1;
        }
        return -1;
      });

      // order answers for short surveys
      for (let j = 0; j < question.answers.length; j += 1) {
        const answer = question.answers[j];
        totalWords += answer.body;
      }

      // sort scorepoints
      if (
        question.style === QUESTION_MATRIX_SINGLE ||
        question.style === QUESTION_MATRIX_MULTIPLE
      ) {
        question.scorepoints.sort((a, b) => {
          if (a.ord > b.ord) {
            return 1;
          }
          return -1;
        });
      }

      // customize NPS min and max texts
      if (
        question.style === QUESTION_NPS &&
        question.answers[0] &&
        question.answers[10]
      ) {
        const minAnswer = question.answers[0];
        const maxAnswer = question.answers[10];
        const minText = Pollfish.ui.getText('NPSmin');
        const maxText = Pollfish.ui.getText('NPSmax');
        // NOTE: we have a case where the same survey is rendered twice and
        // the text was wrong
        if (minAnswer.body.indexOf(minText) === -1) {
          minAnswer.body = `${minAnswer.body} - ${minText}`;
        }
        if (maxAnswer.body.indexOf(maxText) === -1) {
          maxAnswer.body = `${maxAnswer.body} - ${maxText}`;
        }
      }

      // download images for first question only
      if (i === 0) {
        this.downloadImagesForQuestion(question);
      }
    }

    surveyLOI = Math.round(totalWords.split(' ').length / 220);

    if (surveyLOI > 4) {
      surveyLOI = 4;
    } else if (surveyLOI === 0) {
      surveyLOI = 1;
    }

    Pollfish.helper.surveyLOI = surveyLOI;
  }

  shuffleGroups() {
    const { questions } = this.props;

    return questions;
  }

  // we currently only validate other type answers
  static validateAnswers(questionRefId, answers) {
    const errors = {};
    const emptyOtherAnswer = find(answers, (answer = {}) => {
      if (answer.type === ANSWER_OTHER) {
        const otherValue = $.trim(
          $(`#otherContainerInput-${questionRefId}`).val(),
        );
        if (!otherValue) {
          return true;
        }
      }
      return false;
    });

    if (emptyOtherAnswer && emptyOtherAnswer.required) {
      errors.emptyOtherAnswer = true;
    }
    return errors;
  }

  getRedirectURL() {
    const { url } = this.props;
    if (!url) {
      Raven.captureMessage(
        'Unexpected flow: survey redirection url should not be empty',
      );
      return undefined;
    }

    const hasQueryString = url.indexOf('?') !== -1;
    const urlEndsWithAmpersand = url.slice(url.length - 1) === '&';

    const surveySessionId = this.getSurveySessionIdAsQueryString();

    if (!surveySessionId) {
      return url;
    }

    const appendQueryString = [surveySessionId]
      .filter((queryString) => Boolean(queryString))
      .join('&');

    if (hasQueryString && urlEndsWithAmpersand) {
      return `${url}${appendQueryString}`;
    }
    if (hasQueryString && !urlEndsWithAmpersand) {
      return `${url}&${appendQueryString}`;
    }
    return `${url}?${appendQueryString}`;
  }

  hasExpired() {
    return Pollfish.helper.expiredSurveys.indexOf(this.props.surveyId) > -1;
  }

  getStoredAnswers() {
    // the backend expects screening question answers as well
    return [...Pollfish.screening.getStoredAnswers(), ...this.savedAnswers];
  }

  getQuestions() {
    return this.props.questions;
  }

  getQuestionByRefId(refId) {
    let questionRefId = refId;
    if (typeof refId !== 'string') {
      questionRefId = refId.toString();
    }

    const screeningQuestion = Pollfish.screening.getQuestionByRefId(
      questionRefId,
    );
    if (screeningQuestion) {
      return screeningQuestion;
    }

    const question = find(this.props.questions, ['refId', questionRefId]);
    if (question) {
      return question;
    }

    return null;
  }

  getQuestionById(id) {
    let questionId = id;
    if (typeof questionId === 'string') {
      questionId = parseInt(questionId, 10);
    }

    const screeningQuestion = Pollfish.screening.getQuestionById(questionId);
    if (screeningQuestion) {
      return screeningQuestion;
    }

    const question = find(this.props.questions, ['id', questionId]);
    if (question) {
      return question;
    }

    // throw new Error(
    // `tried finding question with id ${questionId} but no such question was found`);
    return null;
  }

  getAnswerById(id, questionContainingAnswer) {
    let answerId = id;
    if (typeof answerId === 'string') {
      answerId = parseInt(answerId, 10);
    }
    // this util is fairly slow and shouldn't be used much
    if (!questionContainingAnswer) {
      let answer;
      // early return if answer is found
      some(this.props.questions, (question) => {
        answer = find(question.answers, ['id', answerId]);
        return answer;
      });

      // check tag questions
      if (!answer) {
        // early return if answer is found
        some(Pollfish.screening.getQuestions(), (question) => {
          answer = find(question.answers, ['id', answerId]);
          return answer;
        });
      }

      return answer;
    }

    const answer = find(questionContainingAnswer.answers, ['id', answerId]);
    if (has(questionContainingAnswer, 'groupRefId')) {
      const group = find(this.groups, [
        'refId',
        questionContainingAnswer.groupRefId,
      ]);
      // since we show conceptsAnswers and not answers, we need to search for them in
      // a different place but we still support the "none of the above" option if existing
      const answerType = get(answer, 'style');
      if (
        group.type === GROUP_TYPE_CONJOINT &&
        answerType !== ANSWER_NONE_OF_THE_ABOVE
      ) {
        const currentChoiceSet = group.getCurrentChoiceSet();
        return find(currentChoiceSet.conceptAnswers, ['id', answerId]);
      }
      if (
        group.type === GROUP_TYPE_MAXDIFF &&
        answerType !== ANSWER_NONE_OF_THE_ABOVE
      ) {
        const currentChoiceSet = group.getCurrentChoiceSet();
        return find(currentChoiceSet.conceptAnswers, ['id', answerId]);
      }
    }
    return answer;
  }

  hasStartedSurvey() {
    return !isEmpty([
      ...Pollfish.screening.getStoredAnswers(),
      ...this.savedAnswers,
    ]);
  }

  getNextHelperQuestion(selectedAnswers) {
    // we currently do not support more than one feedback question
    // I could make this more "clever" by allowing future feedback questions
    // but i am not 100% sure how the rest of the application will behave with more
    // of them
    if (this.props.isFeedbackSurvey) {
      if (!selectedAnswers) {
        return this.props.questions[0];
      }

      return null;
    }

    // we need to send something different than null and falsy
    return undefined;
  }

  async getNextQuestion(selectedAnswers, currentQuestion = {}) {
    if (this.isFinished) {
      return null;
    }
    const { questionnaireLogic } = this.props;

    // Check if conversational AI question is in failure state
    // (either error or cannot get valid input from respondent)
    // and if so, we need to prevent respondent from continuing the survey.
    if (currentQuestion.style === QUESTION_CONVERSATIONAL) {
      const conversationalAnswer = document.querySelector(
        '.answer--conversational-ai[data-status]',
      );
      if (conversationalAnswer) {
        const currentStatus = conversationalAnswer.getAttribute('data-status');
        if (currentStatus === CONVERSATIONAL_AI_VALUE_STATUSES.FAILURE) {
          return null;
        }
      }
    }

    const nextHelperQuestion = this.getNextHelperQuestion(selectedAnswers);
    // we also need to handle the case where we really shouldn't return anything
    if (nextHelperQuestion || nextHelperQuestion === null) {
      return nextHelperQuestion;
    }

    // NOTE: I don't like how this fallback has been implemented here. We/I should probably
    // rewrite it
    if (!questionnaireLogic) {
      // being here means we have exhausted screening
      // questions and we move towards the actual survey questions
      // setup the entry point of the survey based on the head id
      if (!this.hasStartedSurvey()) {
        return [
          ...Pollfish.screening.getQuestions(),
          ...this.props.questions,
        ][0];
      }

      const nextQuestion = this.getNextQuestionWithoutADL(currentQuestion);

      if (!nextQuestion) {
        return this.getLastQuestion();
      }
      return nextQuestion;
    }

    // if the survey is not started and we have a questionnaireLogic object,
    // then grab the first entry point
    if (!this.hasStartedSurvey()) {
      const outcome = questionnaireLogicService.getNextOutcomeForEntryPoint(
        questionnaireLogic,
      );

      if (!isEmpty(outcome.reason)) {
        Pollfish.surveyState.setFailedOutcomeReason(outcome.reason);
      }

      return this.getNextQuestionFromNode(outcome.params.id);
    }

    const outcome = questionnaireLogicService.getOutcomeForNode(
      questionnaireLogic,
      currentQuestion.refId,
    );

    if (!isEmpty(outcome.reason)) {
      Pollfish.surveyState.setFailedOutcomeReason(outcome.reason);
    }

    return this.getNextQuestionFromNode(outcome.params.id);
  }

  async getNextQuestionFromNode(nextNodeRefId) {
    if (nextNodeRefId === FinalNode) {
      return this.getLastQuestion();
    }

    const nextQuestion = find(
      [...Pollfish.screening.getQuestions(), ...this.props.questions],
      ['refId', nextNodeRefId],
    );

    if (
      !!nextQuestion.groupRefId &&
      this.groupRefIds &&
      this.groupRefIds.includes(nextQuestion.groupRefId) &&
      !this.groupsSetUp.includes(nextQuestion.groupRefId)
    ) {
      this.currentGroup = this.groups.find(
        (group) => group.refId === nextQuestion.groupRefId,
      );
      await this.currentGroup.setup();

      this.groupsSetUp.push(nextQuestion.groupRefId);

      if (this.currentGroup.isSkipped()) {
        const outcome = questionnaireLogicService.getOutcomeForNode(
          this.props.questionnaireLogic,
          this.currentGroup.refId,
        );

        Pollfish.store.respondentKnowledge.markNodeAsSkipped(
          this.currentGroup.refId,
          this.props.questionnaireLogic.questionnaire,
        );

        if (!isEmpty(outcome.reason)) {
          Pollfish.surveyState.setFailedOutcomeReason(outcome.reason);
        }

        return this.getNextQuestionFromNode(outcome.params.id);
      }
    }

    return nextQuestion;
  }

  // TODO: clean code and support adl for all
  getNextQuestionWithoutADL(currentQuestion) {
    // it's unlikely a question missing the refId but we handle this case just in case
    let currentQuestionIndex;
    if (currentQuestion.refId) {
      currentQuestionIndex = findIndex(
        [...Pollfish.screening.getQuestions(), ...this.props.questions],
        ['refId', currentQuestion.refId],
      );
    } else if (currentQuestion.id) {
      currentQuestionIndex = findIndex(
        [...Pollfish.screening.getQuestions(), ...this.props.questions],
        ['id', currentQuestion.id],
      );
    }

    if (currentQuestionIndex === -1) {
      throw new Error('Missing Question');
    }
    return [...Pollfish.screening.getQuestions(), ...this.props.questions][
      currentQuestionIndex + 1
    ];
  }

  getDisplaySamePageGroupByQuestion(question) {
    const groupRefId = get(question, 'groupRefId');

    if (groupRefId) {
      const group = find(this.groups, ['refId', groupRefId]);
      if (group.isDisplaySamePage()) {
        return group;
      }
      return undefined;
    }
    return undefined;
  }

  getQuestionsRefIdsByGroup(refId) {
    const group = find(this.groups, ['refId', refId]);
    if (!group) {
      return undefined;
    }
    return map(group.getQuestions(), ['refId']);
  }

  saveAnswer(question, answers) {
    const questionId = question.id;
    const { refId: questionRefId } = question;

    // store selected question to global array
    Pollfish.store.answeredQuestions.push(questionRefId || questionId);
    Pollfish.helper.checkTimeElapsed();

    const rawData = get(Pollfish, 'helper.json.raw', {});
    // create param object to send to server
    // TBD: remove version but also inform backend
    const rewardConversionHash = rawData.reward_conversion_hash || '';
    this.questionOrder += 1;
    const requestObject = {
      s_id: Pollfish.helper.json.s_id,
      ttc: Pollfish.helper.ttc.elapsed,
      reward_name: rawData.reward_name
        ? encodeURIComponent(rawData.reward_name)
        : '',
      reward_conversion: rawData.reward_conversion || '',
      reward_conversion_hash: encodeURIComponent(rewardConversionHash),
      token: Pollfish.helper.getNextCount(),
      q_id: questionId,
      q_ref_id: questionRefId,
      answers: transformAnswersForBackendStorage({
        question,
        answers,
      }),
      survey_session_id: this.getSurveySessionId(),
      questionOrder: this.questionOrder,
      questionStyle: question.style,
    };

    if (question.isScreening) {
      Pollfish.screening.saveAnswer(requestObject, question, answers);
    } else {
      let conceptRefId;
      if (has(question, 'groupRefId')) {
        const group = find(this.groups, ['refId', question.groupRefId]);
        if (
          group.type === GROUP_TYPE_AB ||
          group.type === GROUP_TYPE_VAN_WESTENDORP ||
          group.type === GROUP_TYPE_LOOP_AND_MERGE
        ) {
          conceptRefId = group.getCurrentConcept();
          // in preview mode of a group question whose group will be skipped, use the first
          // available concept to mock save answer
          if (Pollfish.helper.isPreview && !conceptRefId) {
            conceptRefId = get(group, 'concepts[0].refId', '');
          }

          const selectedConcept = find(group.concepts, ['refId', conceptRefId]);
          const selectedConceptId = selectedConcept.id.toString();
          requestObject.attribute_grouping_id = selectedConceptId;
          Pollfish.store.addAnsweredConcept(group.refId, selectedConcept);
        } else if (
          group.type === GROUP_TYPE_CONJOINT ||
          group.type === GROUP_TYPE_MAXDIFF
        ) {
          // do something for conjoint when we allow logic rules
          requestObject.choiceSetBlockId = group.block.id;
          const currentChoiceSet = group.getCurrentChoiceSet();
          requestObject.choiceSetId = currentChoiceSet.id;
          Pollfish.store.addAnsweredChoiceSet(
            group.refId,
            currentChoiceSet.id,
            group.block.id,
          );
        }
      }

      const transformedAnswers = transformAnswersForADL({
        question,
        answers,
      });

      Pollfish.store.respondentKnowledge.appendToCollection(
        KnowledgeKeys.QuestionAnswers,
        {
          questionId: question.refId,
          answers: transformedAnswers,
          conceptId: conceptRefId,
        },
      );

      // we save these answers so that the backend will receive them all together
      // but the backend expects their values as strings s_id, ttc etc
      this.savedAnswers.push(
        encodeTextAnswers(transformValuesToString(requestObject)),
      );
      // send the selected answer to the server
      // we don't need to save the answer for feedback surveys, since we also send it on
      // the survey/reject endpoints
      if (!this.isFeedbackSurvey()) {
        return requestObject;
      }
    }
    return null;
  }

  saveAnswerRequest(requestObject) {
    request({
      url: '/v2/device/save/answers',
      method: 'POST',
      contentType: 'application/json; charset=utf-8',
      transformData: true,
      transformationDataType: TRANSFORMATION_DATA_TYPE_JSON,
      data: requestObject,
    });
  }

  saveGroupAnswerRequest(requestObject) {
    request({
      url: '/v2/device/save/multiple-questions/answers',
      method: 'POST',
      contentType: 'application/json; charset=utf-8',
      transformData: true,
      transformationDataType: TRANSFORMATION_DATA_TYPE_JSON,
      data: requestObject,
    });
  }

  getDistributionId() {
    return encodeURIComponent(this.props.distribution_id);
  }

  setSurveySessionId(id) {
    this.props.surveySessionId = id;
  }

  getSurveySessionId() {
    return this.props.surveySessionId;
  }

  getSurveyRevisionId() {
    return this.props.revisionId;
  }

  getSurveyId() {
    return this.props.surveyId;
  }

  getCustomEndMessage() {
    return this.props.custom_end_message;
  }

  getSurveySessionIdAsQueryString() {
    const surveySessionId = this.getSurveySessionId();
    if (surveySessionId) {
      return `survey_session_id=${surveySessionId}`;
    }
    return '';
  }

  getSurveyLanguage() {
    return this.props.lang;
  }

  updateFallbackValues() {
    /* eslint-disable camelcase */
    const {
      surveyId,
      surveyClass,
      thirdPartyProvider,
      reward_name,
      reward_value,
      reward_conversion,
      reward_conversion_hash,
      survey_price,
      survey_loi,
      survey_ir,
      remaining_completes,
      distribution_id,
      hasCustomTerms,
      // lang,
      // user_lang,
    } = this.props;
    Pollfish.helper.json.s_id = surveyId;
    Pollfish.helper.json.survey_class = surveyClass;
    Pollfish.helper.json.third_party_provider = thirdPartyProvider;
    Pollfish.helper.json.reward_name = reward_name;
    Pollfish.helper.json.reward_value = reward_value;
    Pollfish.helper.json.reward_conversion = reward_conversion;
    Pollfish.helper.json.reward_conversion_hash = reward_conversion_hash;
    Pollfish.helper.json.survey_loi = survey_loi;
    Pollfish.helper.json.survey_ir = survey_ir;
    Pollfish.helper.json.survey_price = survey_price;
    Pollfish.helper.json.remaining_completes = remaining_completes;
    Pollfish.helper.json.distribution_id = distribution_id;
    Pollfish.helper.json.hasCustomTerms = hasCustomTerms;
    if (this.isFeedbackSurvey()) {
      Pollfish.store.surveyRejectTriggerPage = $('body').attr('data-page');
    } else {
      Pollfish.store.surveyRejectTriggerPage = undefined;
    }
    // Pollfish.helper.json.user_lang = user_lang;
    // Pollfish.helper.json.lang = lang;
    /* eslint-disable camelcase */
  }

  getNoCheck() {
    const { noCheck } = this.props;
    if (isBoolean(noCheck)) {
      return noCheck;
    }
    return null;
  }

  setCheckAndEnterSurveyHasBeenCalledOnce() {
    this.checkAndEnterSurveyHasBeenCalledOnce = true;
  }

  getCheckAndEnterSurveyHasBeenCalledOnce() {
    return this.checkAndEnterSurveyHasBeenCalledOnce;
  }
}

export default Survey;
